import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Folder as FolderIcon, FolderPlus } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";
import type { Folder } from "../lib/types";

interface Props {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onCreate: (parentId: string | null) => void;
}

export function FolderTree({ selectedId, onSelect, onCreate }: Props) {
  const { data: folders } = useQuery({
    queryKey: ["folders"],
    queryFn: () => api.listFolders(),
  });

  const roots = (folders ?? []).filter((f) => f.parent_id === null);

  return (
    <div className="border-b border-zinc-800 px-2 py-2 text-sm">
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-500">
          Ordner
        </span>
        <button
          onClick={() => onCreate(null)}
          title="Neuer Ordner"
          className="text-zinc-500 hover:text-emerald-400"
        >
          <FolderPlus className="h-3.5 w-3.5" />
        </button>
      </div>

      <button
        onClick={() => onSelect(null)}
        className={`flex w-full items-center gap-1 rounded px-1 py-1 text-left text-xs ${
          selectedId === null
            ? "bg-zinc-900 text-white"
            : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
        }`}
      >
        <FolderIcon className="h-3 w-3" />
        <span>Alle Notes</span>
      </button>

      {roots.map((f) => (
        <FolderRow
          key={f.id}
          folder={f}
          all={folders ?? []}
          depth={0}
          selectedId={selectedId}
          onSelect={onSelect}
          onCreate={onCreate}
        />
      ))}
    </div>
  );
}

interface RowProps {
  folder: Folder;
  all: Folder[];
  depth: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onCreate: (parentId: string | null) => void;
}

function FolderRow({ folder, all, depth, selectedId, onSelect, onCreate }: RowProps) {
  const [open, setOpen] = useState(true);
  const children = all.filter((f) => f.parent_id === folder.id);

  return (
    <div>
      <div
        className={`group flex items-center gap-1 rounded px-1 py-1 text-xs hover:bg-zinc-900 ${
          selectedId === folder.id ? "bg-zinc-900 text-white" : "text-zinc-300"
        }`}
        style={{ paddingLeft: `${4 + depth * 12}px` }}
      >
        {children.length > 0 ? (
          <button
            onClick={() => setOpen(!open)}
            className="text-zinc-500 hover:text-zinc-200"
          >
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : (
          <span className="w-3" />
        )}
        <FolderIcon className="h-3 w-3 text-zinc-500" />
        <button
          onClick={() => onSelect(folder.id)}
          className="flex-1 truncate text-left"
        >
          {folder.name}
        </button>
        <button
          onClick={() => onCreate(folder.id)}
          title="Unterordner"
          className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-emerald-400"
        >
          <FolderPlus className="h-3 w-3" />
        </button>
      </div>
      {open &&
        children.map((c) => (
          <FolderRow
            key={c.id}
            folder={c}
            all={all}
            depth={depth + 1}
            selectedId={selectedId}
            onSelect={onSelect}
            onCreate={onCreate}
          />
        ))}
    </div>
  );
}
